"use client";

import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import {
  TurnstileVerification,
  type TurnstileVerificationHandle,
} from "@/components/auth/TurnstileVerification";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { addMessage } from "@/components/Messages";
import { env } from "@/env";
import { signInEmailPasswordless } from "@/lib/auth/signInEmailPasswordless";
import { useRouteLanguage, useRouter } from "@/lib/locale/navigation";
import { type ForgotPasswordFormData, ForgotPasswordFormFields } from "./components";

export default function ForgotPasswordPage() {
  const t = useTranslations();
  const router = useRouter();
  const locale = useRouteLanguage();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const emailSubmissionInFlightRef = useRef(false);
  const turnstileTokenRef = useRef<string | null>(null);
  const turnstileRef = useRef<TurnstileVerificationHandle>(null);

  const handleTurnstileTokenChange = (token: string | null) => {
    turnstileTokenRef.current = token;
    setTurnstileToken(token);
  };

  const handleSubmitForm = async (formData: ForgotPasswordFormData) => {
    const submittedToken = turnstileTokenRef.current;
    if (emailSubmissionInFlightRef.current || !submittedToken) return;
    emailSubmissionInFlightRef.current = true;
    turnstileTokenRef.current = null;
    setTurnstileToken(null);
    let returnsControlToForm = true;
    setIsLoading(true);

    try {
      // Redirect to /app/dashboard after magic link verification
      // This must be within the /app/ route which has NhostProvider to process the token
      const redirectTo = new URL(`/${locale}/app/dashboard/`, window.location.href);
      const response = await signInEmailPasswordless(
        formData.email,
        {
          redirectTo: redirectTo.toString(),
        },
        {
          headers: {
            "x-cf-turnstile-response": submittedToken,
          },
        }
      ).catch((error: unknown) => {
        turnstileRef.current?.resetAfterSubmission();
        console.warn(error);
        addMessage({ text: t("auth.forgotPasswordErrorMessage"), type: "ERROR" });
        return null;
      });

      if (!response) return;

      if (response.isSuccess) {
        router.push("/app/auth/post-forgot-password");
        returnsControlToForm = false;
        return <LoadingScreen />;
      }

      turnstileRef.current?.resetAfterSubmission();
      console.warn(response);
      addMessage({
        text: response.error?.message || t("auth.forgotPasswordErrorMessage"),
        type: "ERROR",
      });
    } finally {
      if (returnsControlToForm) {
        emailSubmissionInFlightRef.current = false;
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="t-container-md mt-36 flex items-center justify-center">
      <ForgotPasswordFormFields
        isLoading={isLoading}
        onSubmit={handleSubmitForm}
        turnstileToken={turnstileToken}
        turnstileVerification={
          <TurnstileVerification
            ref={turnstileRef}
            siteKey={env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
            surface="forgot_password"
            onTokenChange={handleTurnstileTokenChange}
          />
        }
      />
    </div>
  );
}
